import React, { useState, useEffect } from 'react';
import { fetchPlayerTeamHistory } from '../services/AccountService';
import { PlayerTeamHistory } from '../models/PlayerTeamHistory';
import { fetchTeamByTeamIdAndSeason } from '../services/TeamService';
import { Team } from '../models/Team';
import { divisionMappings } from '../Constants';

interface CompactTeamHistory {
    season: number;
    teamId: string;
    teamName: string;
    teamLogo: string;
    division?: string;
    rating: number;
  }
  
  interface PlayerTeamHistoryComponentProps {
    steamId: string;
    isDark: boolean;
    compact?: boolean;
  }
  
  const PlayerTeamHistoryComponent: React.FC<PlayerTeamHistoryComponentProps> = ({ 
    steamId, 
    isDark,
    compact = false
  }) => {
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState<CompactTeamHistory[]>([]);
    const [error, setError] = useState<string | null>(null);
  
    useEffect(() => {
      const fetchHistory = async () => {
        try {
          setLoading(true);
          
          // Fetch player's team history
          const historyData = await fetchPlayerTeamHistory(steamId);
          
          // Get team details for each history entry
          const historyWithTeams = await Promise.all(
            historyData.map(async (entry: PlayerTeamHistory) => {
              let team: Team | null = null;
              try {
                team = await fetchTeamByTeamIdAndSeason(entry.teamId, entry.season);
              } catch (err) {
                console.error(`Failed to fetch team for ${entry.teamId} in season ${entry.season}`, err);
              }
              
              return {
                season: entry.season,
                teamId: entry.teamId,
                teamName: team?.teamName || 'Unknown Team',
                teamLogo: team?.teamLogo || '',
                division: team?.division,
                rating: entry.rating !== -1 ? entry.rating : 0,
              };
            })
          );
          
          // Sort by season (descending) and set state
          historyWithTeams.sort((a, b) => b.season - a.season);
          setHistory(historyWithTeams);
          setError(null);
        } catch (err) {
          console.error('Error fetching player team history:', err);
          setError('Failed to load team history');
        } finally {
          setLoading(false);
        }
      };
  
      fetchHistory();
    }, [steamId]);
  
    // Convert division to a simple letter/number
    const getSimpleDivision = (division?: string): string => {
      if (!division) return '-';
      
      if (division === 'ligaen') return 'L';
      
      // For divisions like 1div, 2divA, 3divB, etc.
      if (division.startsWith('1div')) return '1';
      if (division.startsWith('2div')) {
        // Check if there's a letter at the end (like A or B)
        const letter = division.charAt(4);
        return letter && letter.match(/[A-Z]/i) ? `2${letter}` : '2';
      }
      if (division.startsWith('3div')) {
        const letter = division.charAt(4);
        return letter && letter.match(/[A-Z]/i) ? `3${letter}` : '3';
      }
      if (division.startsWith('4div')) {
        const letter = division.charAt(4);
        return letter && letter.match(/[A-Z]/i) ? `4${letter}` : '4';
      }
      if (division.startsWith('5div')) {
        const letter = division.charAt(4);
        return letter && letter.match(/[A-Z]/i) ? `5${letter}` : '5';
      }
      
      // Fallback for any other format
      return division.substring(0, 2);
    };
  
    if (loading) {
      return <div className="text-center"><small>Loading...</small></div>;
    }
  
    if (error) {
      return <div className="text-center text-danger"><small>Error!</small></div>;
    }
  
    if (history.length === 0) {
      return <div className="text-center"><small>No history</small></div>;
    }
  
    return (
      <div className={`player-history-compact ${isDark ? 'text-white' : ''}`} style={{ fontSize: compact ? '0.8rem' : '0.875rem' }}>
        <table className={`table table-sm ${isDark ? 'table-dark' : ''} mb-0`}>
          <thead>
            <tr>
              <th>S</th>
              <th>Team</th>
              <th>Div</th>
              <th>Rat</th>
            </tr>
          </thead>
          <tbody>
            {history.map((entry, index) => (
              <tr key={`${entry.teamId}-${entry.season}`}>
                <td>{entry.season}</td>
                <td>
                  <img 
                    src={entry.teamLogo} 
                    alt={entry.teamName} 
                    title={entry.teamName}
                    style={{ height: '28px', width: '28px', objectFit: 'contain' }} 
                  />
                </td>
                <td>
                  {getSimpleDivision(entry.division)}
                </td>
                <td>{entry.rating > 0 ? entry.rating.toFixed(2) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  export default PlayerTeamHistoryComponent;